import React from 'react'
import { useDispatch } from 'react-redux'

import { Col, Modal, ProgressBar, Row } from 'react-bootstrap'
import { faSave } from '@fortawesome/free-solid-svg-icons'

import { Button, Paragraph, SwitchV2 } from 'src/components'

import { showAlert } from 'src/actions/alert.actions'
import { handlerError, handlerSuccess } from 'src/selectors/error.selector'

import { useModuleVersionConfig } from './useModuleVersionConfig'

interface IProps {
  show: boolean
  companyId: number | null
  companyName?: string | null
  onHide: () => void
}

interface GroupedModules {
  id: number
  name: string
  sequence: number
  modules: ModuleVersion[]
}

const NO_GROUP = { id: -1, name: 'Sin categoría', sequence: Number.MAX_SAFE_INTEGER }

/**
 * Agrupa los módulos por `group.id` (los que vienen sin grupo caen en "Sin
 * categoría"), ordena los grupos por `sequence` y, dentro de cada uno, los
 * módulos alfanuméricamente por nombre.
 */
const groupModules = (modules: ModuleVersion[]): GroupedModules[] => {
  const byId = new Map<number, GroupedModules>()
  modules.forEach(module => {
    const group = module.group ?? NO_GROUP
    if (!byId.has(group.id)) byId.set(group.id, { ...group, modules: [] })
    byId.get(group.id).modules.push(module)
  })
  return Array.from(byId.values())
    .sort((a, b) => a.sequence - b.sequence || a.name.localeCompare(b.name))
    .map(group => ({
      ...group,
      modules: group.modules.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true }),
      ),
    }))
}

/**
 * Panel admin (ATL-743): define, por empresa, qué módulos se muestran en la
 * versión 2 (nueva interfaz). El interruptor encendido = v2; apagado = v1.
 */
const ModuleVersionConfig = ({ show, companyId, companyName, onHide }: IProps) => {
  const dispatch = useDispatch()
  const { modules, loading, saving, toggle, setAll, save, hasChanges } =
    useModuleVersionConfig(companyId, show)

  const groups = groupModules(modules)

  const onSave = async () => {
    const result = await save()
    if (result.ok) {
      dispatch(showAlert(handlerSuccess('Configuración de módulos actualizada.')))
      onHide()
    } else {
      dispatch(showAlert(handlerError('No se pudo guardar la configuración.')))
    }
  }

  return (
    <Modal show={show} size={'lg'} centered onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>Configuración de módulos {companyName || ''}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading && <ProgressBar animated now={100} label={'Cargando...'} />}
        <Row>
          <Col xl={12}>
            <Paragraph>
              Activa cada módulo para mostrarlo en la versión 2 (nueva interfaz). Los
              módulos apagados se muestran en la versión 1.
            </Paragraph>
          </Col>
          {!loading && modules.length > 0 && (
            <Col xl={12}>
              <Row className={'container-buttons'}>
                <Button
                  color={'secondary'}
                  disabled={saving}
                  onClick={() => setAll(true)}>
                  Activar todo
                </Button>
                <Button
                  color={'secondary'}
                  disabled={saving}
                  onClick={() => setAll(false)}>
                  Desactivar todo
                </Button>
              </Row>
            </Col>
          )}
          {!loading && modules.length === 0 && (
            <Col xl={12}>
              <Paragraph dim>No hay módulos configurables para esta empresa.</Paragraph>
            </Col>
          )}
          {groups.map(group => (
            <Col xl={12} key={group.id}>
              <Paragraph bold className={'mt-2'}>
                {group.name}
              </Paragraph>
              <Row>
                {group.modules.map(module => (
                  <Col xl={6} lg={6} md={12} key={module.moduleId}>
                    <SwitchV2
                      checked={module.v2}
                      onChange={() => toggle(module.moduleId)}
                      label={module.name}
                      info={module.v2 ? 'Versión 2' : 'Versión 1'}
                      disabled={saving}
                    />
                  </Col>
                ))}
              </Row>
            </Col>
          ))}
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Row className={'container-buttons'}>
          <Button
            icon={faSave}
            loading={saving}
            disabled={loading || !hasChanges}
            onClick={onSave}>
            Guardar
          </Button>
        </Row>
      </Modal.Footer>
    </Modal>
  )
}
export default ModuleVersionConfig
